// src/components/ProductCard.jsx
import React, { useState, useEffect, useRef } from 'react';
import styles from './ProductCard.module.css';
import { motion } from 'framer-motion';

const ProductCard = ({ productId, image, name, size, sku, sorts = [], onSelectionChange, action }) => {
  const [selectedSortName, setSelectedSortName] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState('');
  const isFirstRender = useRef(true); // Track the first render

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (selectedSortName && selectedColor && selectedQuantity) {
      // Notify parent only when sortName, color, and quantity are selected
      onSelectionChange({
        productId, // Use productId instead of SKU
        name,
        sortName: selectedSortName, // Renamed from 'sort' to 'sortName'
        color: selectedColor,
        quantity: parseInt(selectedQuantity, 10) || 0, // Ensure the quantity is a number (default to 0)
      });
    }
  }, [selectedSortName, selectedColor, selectedQuantity, productId, name, onSelectionChange]);

  // Find the selected sort object
  const selectedSortObj = sorts.find((sortObj) => sortObj.sortName === selectedSortName);

  // Convert image buffer to base64 string if image data exists
  const getImageSrc = () => {
    if (image && image.data && image.contentType) {
      try {
        // Access the byte array correctly
        const byteArray = Array.isArray(image.data.data) ? image.data.data : [];

        // Ensure byteArray is indeed an array of numbers
        if (!Array.isArray(byteArray)) {
          console.error('image.data.data is not an array');
          return '';
        }

        // Convert the byte array to a binary string
        const binaryString = byteArray.reduce((acc, byte) => acc + String.fromCharCode(byte), '');

        // Encode the binary string to base64
        const base64String = window.btoa(binaryString);

        return `data:${image.contentType};base64,${base64String}`;
      } catch (error) {
        console.error('Error converting image data to base64:', error);
        return '';
      }
    }
    return '';
  };

  return (
    <motion.article
      className={styles.productCard}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <div className={styles.productInfo}>
        {image && image.data && image.contentType ? (
          <img
            loading="lazy"
            src={getImageSrc()} // Display the image from base64 data
            alt={`${name}`} // Updated alt attribute without redundant words
            className={styles.productImage}
          />
        ) : (
          <div className={styles.placeholderImage}>No Image</div>
        )}
        <div className={styles.productDetails}>
          <h2 className={styles.productName}>{name}</h2>
          <p className={styles.productSize}>Size: {size}</p>
          <p className={styles.productCode}>SKU: {sku}</p>
        </div>
      </div>
      <div className={styles.productOptions}>
        {/* Dropdown for selecting a sort */}
        <select
          id={`sort-select-${productId}`}
          className={styles.sortDropdown}
          value={selectedSortName}
          onChange={(e) => {
            setSelectedSortName(e.target.value);
            setSelectedColor(''); // Reset color when sortName changes
          }}
        >
          <option value="">Select a sort</option>
          {sorts.map((sortObj, index) => (
            <option key={sortObj.sortName || index} value={sortObj.sortName}>
              {sortObj.sortName}
            </option>
          ))}
        </select>

        {/* Dropdown for selecting a color */}
        <select
          id={`color-select-${productId}`}
          className={styles.colorDropdown}
          value={selectedColor}
          onChange={(e) => setSelectedColor(e.target.value)}
          disabled={!selectedSortName} // Disable if no sortName is selected
        >
          <option value="">Select a color</option>
          {(selectedSortObj?.colors || []).map((colorObj, cIndex) => (
            <option key={colorObj.color || cIndex} value={colorObj.color}>
              {colorObj.color}
            </option>
          ))}
        </select>

        {/* Input for entering quantity */}
        <input
          type="number"
          className={styles.countInput}
          placeholder="Quantity"
          min="1"
          value={selectedQuantity}
          onChange={(e) => setSelectedQuantity(e.target.value)}
          disabled={!selectedColor} // Disable if no color is selected
        />
      </div>
    </motion.article>
  );
};

export default ProductCard;
